import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as AlfredoStyles from "../../styles/layouts/layouts";
import * as AlfredoInterfaces from "../../interfaces";
import agenteService, {
  rotuloDaFerramenta,
  type MensagemDaConversa,
} from "../../services/agente.service";

const pages: AlfredoInterfaces.Pages[] = [
  {
    title: "Início",
    icon: "home",
    options: ["Dashboard", "Calendário"],
    path: "/",
  },
  {
    title: "Relatórios",
    icon: "picture_as_pdf",
    options: ["Contas", "Materiais"],
    path: "/reports",
    items: [
      {
        title: "Contas",
        icon: "list_alt",
        // `options` é DECORATIVO neste componente — não é renderizado. Quem
        // navega é `items[].path`. "Clientes" estava aqui em `options` desde
        // sempre e não tinha rota: aparecia como promessa e não levava a lugar
        // nenhum. Agora tem rota, e a rota explica o próprio bloqueio.
        options: ["Ordens", "Caixa", "Clientes"],
        path: "/reports/orders",
        items: [
          {
            title: "Ordens",
            icon: "receipt_long",
            options: [],
            path: "/reports/orders",
          },
          {
            title: "Caixa",
            icon: "point_of_sale",
            options: [],
            path: "/reports/cash",
          },
          {
            title: "Clientes",
            icon: "groups",
            options: [],
            path: "/reports/customers",
          },
        ],
      },
      {
        title: "Materiais",
        icon: "inventory",
        options: ["Estoque", "Movimentação"],
        path: "/reports/materials",
        items: [
          {
            title: "Estoque",
            icon: "inventory_2",
            options: [],
            path: "/reports/materials",
          },
          {
            title: "Movimentação",
            icon: "swap_horiz",
            options: [],
            path: "/reports/moviment",
          },
        ],
      },
    ],
  },
  {
    title: "Suprimentos",
    icon: "inventory",
    options: ["Compras", "Materiais"],
    path: "/supply",
    items: [
      {
        title: "Compras",
        icon: "local_shipping",
        options: ["Requisição", "Ordens"],
        path: "/supply/requisition",
      },
      {
        title: "Materiais",
        icon: "precision_manufacturing",
        options: ["Estoque", "Movimentação"],
        path: "/supply/moviment",
      },
    ],
  },
  {
    title: "Configuração",
    icon: "settings",
    // `options` é DADO MORTO neste componente: `renderPages` lê só
    // title/icon/path/items. A entrada anunciava "Usuários" e "Permissões" —
    // duas coisas que a tela real nem tem — e o item levava sempre para a
    // primeira aba, porque a aba não era legível pela URL.
    //
    // Agora as abas REAIS estão em `items`, com `path` que resolve: a
    // Configuração hidrata `?aba=` da URL, então cada linha daqui abre
    // exatamente a tela que promete.
    options: [
      "Lojas",
      "Cardápio",
      "Suprimentos",
      "Descontos e Taxas",
      "Comunicações",
      "Colaboradores",
      "Avançado",
    ],
    path: "/settings",
    items: [
      {
        title: "Lojas",
        icon: "storefront",
        options: [],
        path: "/settings?aba=Lojas",
      },
      {
        title: "Cardápio",
        icon: "restaurant_menu",
        options: [],
        path: "/settings?aba=Card%C3%A1pio",
      },
      {
        title: "Suprimentos",
        icon: "inventory",
        options: [],
        path: "/settings?aba=Suprimentos",
      },
      {
        title: "Descontos e Taxas",
        icon: "local_offer",
        options: [],
        path: "/settings?aba=Descontos%20e%20Taxas",
      },
      {
        title: "Comunicações",
        icon: "forum",
        options: [],
        path: "/settings?aba=Comunica%C3%A7%C3%B5es",
      },
      {
        title: "Colaboradores",
        icon: "badge",
        options: [],
        path: "/settings?aba=Colaboradores",
      },
      {
        title: "Avançado",
        icon: "tune",
        options: [],
        path: "/settings?aba=Avan%C3%A7ado",
      },
    ],
  },
];

/**
 * As perguntas sugeridas. Eram três blocos de JSX repetidos, cada um mandando
 * a string literal "Template 1" / "Template 2" / "Template 3" para o chat —
 * ou seja, o botão mostrava a pergunta e enviava outra coisa. Agora o rótulo
 * É a pergunta, e é ele que vai.
 */
const PERGUNTAS_SUGERIDAS = [
  "Quantas compras pendentes eu tenho que resolver?",
  "Qual saldo de massa no estoque da cozinha da loja number one?",
  "Qual faturamento do dia?",
];

const LeftBar: React.FC<AlfredoInterfaces.SidebarProps> = ({ isOpen }) => {
  const navigate = useNavigate();
  const [openDropdowns, setOpenDropdowns] = useState<string[]>([]);
  /*
   * A CONVERSA — antes e depois.
   *
   * Antes: `handleSendMessage` fazia `setMessages([...messages, {…}])` e PARAVA
   * ali. A mensagem do usuário entrava na lista e nada respondia; os três
   * botões de sugestão mandavam as strings "Template 1/2/3". Não existia rota
   * de IA no backend — o chat era casca.
   *
   * Agora: `POST /agente/conversa`, com o histórico inteiro (o servidor corta
   * nas últimas 12). O agente responde com o texto E com a lista de
   * ferramentas que ele consultou, e a tela mostra as duas coisas — resposta
   * que não diz de onde saiu o número é a mesma coisa que a Home inventada que
   * este trabalho acabou de desfazer.
   */
  const [messages, setMessages] = useState<
    { text: string; sender: "user" | "gpt"; fontes?: string[] }[]
  >([]);
  const [inputValue, setInputValue] = useState("");
  const [pensando, setPensando] = useState(false);
  const fimDasMensagensRef = useRef<HTMLDivElement | null>(null);
  /*
   * O corte de texto em JavaScript foi REMOVIDO daqui.
   *
   * `adjustTextForTwoLines` media a altura de linha do botão e ia tirando UMA
   * LETRA POR VEZ da string, colando "..." no fim, até o texto caber em duas
   * linhas de uma caixa de altura fixa. Era o que produzia, na tela do
   * Guilherme, "Quantas compras pend...", "Qual saldo de massa no esto..." e
   * "Qual faturamento d...". O botão agora tem altura automática e o texto
   * quebra (ver `TemplateButton` em styles/layouts/layouts.ts) — não há mais
   * nada a cortar, e por isso as três refs também saíram.
   */

  const toggleDropdown = (path: string) => {
    setOpenDropdowns((prevState) =>
      prevState.includes(path)
        ? prevState.filter((item) => item !== path)
        : [...prevState, path]
    );
  };

  /*
   * A ALTURA DA LISTA DE MENSAGENS TAMBÉM SAIU DO JAVASCRIPT.
   *
   * Era `window.innerHeight - 418` (mais 10px por menu aberto), recalculada num
   * listener de resize. O 418 é a soma dos pedaços em 1440px — em qualquer
   * outra altura de janela a conta erra, e a caixa de perguntas saía por baixo
   * do rodapé. Agora a barra é uma coluna flex e o chat gruda embaixo com
   * `margin-top: auto`: nenhuma medida, nenhum listener, e certo em qualquer
   * altura.
   */

  const perguntar = useCallback(
    async (pergunta: string) => {
      const texto = pergunta.trim();
      if (!texto || pensando) return;

      const comUsuario = [
        ...messages,
        { text: texto, sender: "user" as const },
      ];
      setMessages(comUsuario);
      setInputValue("");
      setPensando(true);

      // O histórico que vai para o servidor é a conversa inteira até aqui — é
      // o que permite "e no mês passado?" depois de "qual o faturamento?".
      const historico: MensagemDaConversa[] = comUsuario.map((m) => ({
        papel: m.sender === "user" ? "user" : "assistant",
        texto: m.text,
      }));

      try {
        const resposta = await agenteService.conversar(historico);
        setMessages((antes) => [
          ...antes,
          {
            text:
              resposta.texto?.trim() ||
              "O assistente não devolveu resposta para esta pergunta.",
            sender: "gpt",
            fontes: Array.from(
              new Set(
                (resposta.passos ?? [])
                  .filter((p) => p.ok)
                  .map((p) => rotuloDaFerramenta(p.nome)),
              ),
            ),
          },
        ]);
      } catch (falha: any) {
        // O erro do servidor é MOSTRADO, não engolido: quando falta a chave do
        // modelo, por exemplo, ele diz exatamente isso.
        setMessages((antes) => [
          ...antes,
          {
            text:
              falha?.response?.data?.message ??
              "Não consegui falar com o assistente agora.",
            sender: "gpt",
          },
        ]);
      } finally {
        setPensando(false);
      }
    },
    [messages, pensando],
  );

  const handleSendMessage = () => {
    void perguntar(inputValue);
  };

  const handleTemplateClick = (pergunta: string) => {
    void perguntar(pergunta);
  };

  // Rolar para a última mensagem. Sem isto a resposta nasce fora da vista, e o
  // usuário acha que nada aconteceu.
  useEffect(() => {
    fimDasMensagensRef.current?.scrollIntoView({ block: "end" });
  }, [messages, pensando]);

  /**
   * Caminho + query, para comparar com o `path` do item.
   *
   * As abas da Configuração viraram links com `?aba=`, e comparar só o
   * `pathname` faria as SETE aparecerem realçadas ao mesmo tempo — todas
   * moram em /settings. Sem query na URL, o comportamento é o de antes.
   */
  const enderecoAtual = () =>
    window.location.pathname + window.location.search;

  const renderPages = (pages: AlfredoInterfaces.Pages[], level = 0) => {
    return pages.map((page) => (
      <div key={page.path}>
        <AlfredoStyles.PageButton
          selected={enderecoAtual() === page.path}
          onClick={() => navigate(page.path)}
          level={level}
        >
          <AlfredoStyles.TopBarMaterialIcons
            selected={enderecoAtual() === page.path}
          >
            {page.icon}
          </AlfredoStyles.TopBarMaterialIcons>
          <span>{page.title}</span>
          {page.items && page.items.length !== 0 ? (
            <AlfredoStyles.TopBarMaterialIcons
              onClick={(e) => {
                e.stopPropagation();
                toggleDropdown(page.path);
              }}
            >
              {openDropdowns.includes(page.path)
                ? "expand_less"
                : "expand_more"}
            </AlfredoStyles.TopBarMaterialIcons>
          ) : (
            <AlfredoStyles.TopBarMaterialIcons>
              grain
            </AlfredoStyles.TopBarMaterialIcons>
          )}
        </AlfredoStyles.PageButton>
        {page.items &&
          openDropdowns.includes(page.path) &&
          renderPages(page.items, level + 1)}
      </div>
    ));
  };

  return (
    <AlfredoStyles.LeftSidebarContainer isOpen={isOpen}>
      {renderPages(pages)}

      <AlfredoStyles.ChatContainer>
        <AlfredoStyles.MessagesContainer>
          {messages.length === 0 && !pensando && (
            <AlfredoStyles.ChatVazio>
              Pergunte sobre a sua rede. O assistente consulta os MESMOS
              relatórios que estas telas mostram — e diz quando o dado não
              existe, em vez de inventar um número.
            </AlfredoStyles.ChatVazio>
          )}
          {messages.map((message, index) => (
            <AlfredoStyles.Message key={index} sender={message.sender}>
              {message.text}
              {message.fontes && message.fontes.length > 0 && (
                <AlfredoStyles.FontesDaResposta>
                  consultou: {message.fontes.join(" · ")}
                </AlfredoStyles.FontesDaResposta>
              )}
            </AlfredoStyles.Message>
          ))}
          {pensando && (
            <AlfredoStyles.Message sender="gpt" aria-live="polite">
              Consultando os relatórios…
            </AlfredoStyles.Message>
          )}
          <div ref={fimDasMensagensRef} />
        </AlfredoStyles.MessagesContainer>
        <AlfredoStyles.PromptContainer>
          <AlfredoStyles.TemplatesContainer>
            {PERGUNTAS_SUGERIDAS.map((pergunta) => (
              <AlfredoStyles.TemplateButton
                key={pergunta}
                type="button"
                title={pergunta}
                disabled={pensando}
                onClick={() => handleTemplateClick(pergunta)}
              >
                {pergunta}
              </AlfredoStyles.TemplateButton>
            ))}
          </AlfredoStyles.TemplatesContainer>
          <AlfredoStyles.InputContainer>
            <AlfredoStyles.MessageInput
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Faça uma pergunta..."
              disabled={pensando}
              // Enter manda; Shift+Enter quebra linha. Sem isto a única forma
              // de enviar era o botão.
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <AlfredoStyles.ChatButton
              type="button"
              onClick={handleSendMessage}
              disabled={pensando || !inputValue.trim()}
              aria-label="Enviar pergunta"
            >
              {pensando ? "hourglass_top" : "send"}
            </AlfredoStyles.ChatButton>
          </AlfredoStyles.InputContainer>
        </AlfredoStyles.PromptContainer>
      </AlfredoStyles.ChatContainer>
    </AlfredoStyles.LeftSidebarContainer>
  );
};

export default LeftBar;
